import { useState } from 'react'
import {
  ThumbsUp, MessageCircle, Share2,
  MapPin, Briefcase, GraduationCap,
  Building2, Clock, CheckCircle, IndianRupee, Send,
} from 'lucide-react'
import { Job } from '../../types'
import { formatDistanceToNow } from 'date-fns'
import { Link } from 'react-router-dom'
import { clsx } from 'clsx'

interface Props {
  job: Job
  onLike?: (jobId: number) => void
  showApplyButton?: boolean
  onApply?: (job: Job) => void
}

export default function JobCard({ job, onLike, showApplyButton = true, onApply }: Props) {
  const isFree      = job.referralFeePaise === 0
  const feeRupees   = job.referralFeePaise / 100
  const [expanded, setExpanded] = useState(false)

  const description = job.description || ''
  const isLong      = description.length > 220
  const poster      = (job as any).employee
  const posterName  = poster?.displayName || poster?.email

  return (
    <div
      className="bg-white rounded-2xl overflow-hidden transition-all duration-200"
      style={{
        border: '1px solid #EDE9FE',
        boxShadow: '0 2px 12px rgba(139,92,246,0.08)',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 8px 28px rgba(139,92,246,0.16)'
        e.currentTarget.style.borderColor = '#C4B5FD'
        e.currentTarget.style.transform = 'translateY(-1px)'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = '0 2px 12px rgba(139,92,246,0.08)'
        e.currentTarget.style.borderColor = '#EDE9FE'
        e.currentTarget.style.transform = 'translateY(0)'
      }}
    >
      {/* ── Header ── */}
      <div className="p-5">
        <div className="flex items-start gap-3">

          {/* Company logo */}
          <div className="w-12 h-12 rounded-xl flex items-center justify-center overflow-hidden flex-shrink-0" style={{ background: '#F5F3FF', border: '1px solid #EDE9FE' }}>
            {job.company?.logoUrl ? (
              <img src={job.company.logoUrl} alt={job.company.name} className="w-full h-full object-cover" />
            ) : (
              <Building2 size={22} style={{ color: '#C4B5FD' }} />
            )}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm leading-tight truncate" style={{ color: '#13082A' }}>
              {job.company?.name}
            </p>
            {posterName && (
              <p className="text-xs mt-0.5" style={{ color: '#6B6080' }}>
                Posted by <span className="font-medium" style={{ color: '#7C3AED' }}>{posterName}</span>
              </p>
            )}
            <p className="text-xs mt-0.5 flex items-center gap-1" style={{ color: '#A89EC0' }}>
              <Clock size={11} />
              {formatDistanceToNow(new Date(job.createdAt), { addSuffix: true })}
            </p>
          </div>

          {/* Fee badge */}
          <span
            className="flex-shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold"
            style={isFree
              ? { background: '#D1FAE5', color: '#065F46' }
              : { background: '#EDE9FE', color: '#6D28D9' }
            }
          >
            {isFree ? (
              <><CheckCircle size={11} /> Free</>
            ) : (
              <><IndianRupee size={11} />{feeRupees.toLocaleString()}</>
            )}
          </span>
        </div>

        {/* ── Job Title ── */}
        <Link to={`/jobs/${job.id}`} className="no-underline group block mt-4">
          <h2 className="text-base font-bold leading-snug transition-colors" style={{ color: '#13082A' }}
            onMouseEnter={(e) => { (e.target as HTMLElement).style.color = '#7C3AED' }}
            onMouseLeave={(e) => { (e.target as HTMLElement).style.color = '#13082A' }}
          >
            {job.title}
          </h2>
        </Link>

        {/* ── Meta pills ── */}
        <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-2 text-xs" style={{ color: '#6B6080' }}>
          {job.location && (
            <span className="flex items-center gap-1.5">
              <MapPin size={12} style={{ color: '#A89EC0' }} />{job.location}
            </span>
          )}
          {job.jobType && (
            <span className="flex items-center gap-1.5">
              <Briefcase size={12} style={{ color: '#A89EC0' }} />{job.jobType}
            </span>
          )}
          {(job.experienceMin !== undefined || job.experienceMax !== undefined) && (
            <span className="flex items-center gap-1.5">
              <GraduationCap size={12} style={{ color: '#A89EC0' }} />
              {job.experienceMin}–{job.experienceMax} yrs
            </span>
          )}
        </div>

        {/* ── Description ── */}
        <div className="mt-3 text-sm leading-relaxed" style={{ color: '#6B6080' }}>
          <p className={clsx(!expanded && isLong && 'line-clamp-3')}>{description}</p>
          {isLong && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-xs font-semibold mt-1 transition-colors"
              style={{ color: '#8B5CF6' }}
            >
              {expanded ? 'See less ↑' : '...see more'}
            </button>
          )}
        </div>

        {/* ── Skills ── */}
        {job.skills?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3.5">
            {job.skills.slice(0, 5).map((skill) => (
              <span key={skill} className="skill-chip">{skill}</span>
            ))}
            {job.skills.length > 5 && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" style={{ background: '#F5F3FF', color: '#A89EC0' }}>
                +{job.skills.length - 5} more
              </span>
            )}
          </div>
        )}

        {/* ── Referral fee notice ── */}
        <div
          className="mt-4 flex items-center gap-2 text-xs px-3.5 py-2.5 rounded-xl"
          style={isFree
            ? { background: '#D1FAE5', color: '#065F46' }
            : { background: '#EDE9FE', color: '#5B21B6' }
          }
        >
          {isFree
            ? <><CheckCircle size={13} className="flex-shrink-0" /><span>Free referral — no fee required to apply</span></>
            : <><IndianRupee size={13} className="flex-shrink-0" /><span>Referral bonus: <strong>₹{feeRupees.toLocaleString()}</strong> held in escrow until hiring</span></>
          }
        </div>
      </div>

      {/* ── Stats row ── */}
      <div className="px-5 py-2 flex items-center justify-between text-xs" style={{ borderTop: '1px solid #EDE9FE', color: '#A89EC0' }}>
        <div className="flex items-center gap-1.5">
          <span className="w-4 h-4 rounded-full flex items-center justify-center" style={{ background: '#8B5CF6' }}>
            <ThumbsUp size={9} className="text-white" fill="white" />
          </span>
          <span>{job.likesCount} {job.likesCount === 1 ? 'like' : 'likes'}</span>
        </div>
        <span>{job.commentsCount} comment{job.commentsCount !== 1 ? 's' : ''}</span>
      </div>

      {/* ── Action bar ── */}
      <div className="flex items-center px-2 py-1.5 gap-1" style={{ borderTop: '1px solid #EDE9FE' }}>
        <button
          onClick={() => onLike?.(job.id)}
          className={clsx(
            'flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold transition-all duration-150 flex-1 justify-center',
          )}
          style={job.likedByMe
            ? { background: '#EDE9FE', color: '#7C3AED' }
            : { color: '#6B6080' }
          }
          onMouseEnter={(e) => { if (!job.likedByMe) (e.currentTarget as HTMLElement).style.background = '#F5F3FF' }}
          onMouseLeave={(e) => { if (!job.likedByMe) (e.currentTarget as HTMLElement).style.background = 'transparent' }}
        >
          <ThumbsUp size={15} fill={job.likedByMe ? 'currentColor' : 'none'} />
          Like
        </button>

        <Link
          to={`/jobs/${job.id}#comments`}
          className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold no-underline flex-1 justify-center transition-all duration-150"
          style={{ color: '#6B6080' }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = '#F5F3FF' }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent' }}
        >
          <MessageCircle size={15} />
          Comment
        </Link>

        <button
          className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold flex-1 justify-center transition-all duration-150"
          style={{ color: '#6B6080' }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = '#F5F3FF' }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent' }}
        >
          <Share2 size={15} />
          Share
        </button>

        {showApplyButton && (
          <button
            onClick={() => onApply?.(job)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold text-white transition-all duration-150 flex-shrink-0"
            style={{ background: '#8B5CF6', boxShadow: '0 3px 10px rgba(139,92,246,0.35)' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = '#7C3AED'; (e.currentTarget as HTMLElement).style.transform = 'translateY(-1px)' }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = '#8B5CF6'; (e.currentTarget as HTMLElement).style.transform = 'translateY(0)' }}
          >
            <Send size={13} />
            Apply
          </button>
        )}
      </div>
    </div>
  )
}
